import { useDeferredValue, useEffect, useId, useMemo, useState } from 'react'
import { LookupResults } from '@/components/lookup-results'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from '@/components/ui/table'
import { MergePeopleSheet } from '@/components/people/merge-people-sheet'
import { PersonDetailSheet } from '@/components/people/person-detail-sheet'
import { PersonCreateSheet } from '@/components/people/person-create-sheet'
import { usePeopleSearch } from '@/api/hooks'
import type { MergePeopleOutput, TPerson } from '@schemas'
import { cn } from '@/lib/utils'

type MemberFilter = 'all' | 'members' | 'non-members'

export default function PeoplePage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all')
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeTarget, setMergeTarget] = useState<TPerson | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailPerson, setDetailPerson] = useState<TPerson | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [highlightPersonId, setHighlightPersonId] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const pageSize = 25

  const deferredTerm = useDeferredValue(searchTerm)
  const trimmed = deferredTerm.trim()

  const params = useMemo(() => {
    const next: { q?: string; member?: 'true' | 'false'; page?: number; pageSize?: number } = {
      page,
      pageSize,
    }
    if (trimmed.length >= 1) next.q = trimmed
    if (memberFilter === 'members') next.member = 'true'
    if (memberFilter === 'non-members') next.member = 'false'
    return next
  }, [memberFilter, trimmed, page])

  useEffect(() => {
    setPage(1)
  }, [memberFilter, trimmed])

  const peopleQuery = usePeopleSearch(params, { enabled: true, retry: false })
  const resultsId = useId()
  const people = peopleQuery.data?.people ?? []
  const pagination = peopleQuery.data?.pagination
  const currentPage = pagination?.page ?? page
  const totalPages = pagination?.totalPages ?? 1
  const totalPeople = pagination?.total ?? people.length

  const handleOpenMerge = (person: TPerson) => {
    setMergeTarget(person)
    setMergeOpen(true)
  }

  const handleOpenDetail = (person: TPerson) => {
    setDetailPerson(person)
    setDetailOpen(true)
  }

  const handleDetailOpenChange = (open: boolean) => {
    setDetailOpen(open)
    if (!open) {
      setDetailPerson(null)
    }
  }

  useEffect(() => {
    if (!highlightPersonId) return
    const timer = setTimeout(() => setHighlightPersonId(null), 6000)
    return () => clearTimeout(timer)
  }, [highlightPersonId])

  const handleCreatedPerson = (person: TPerson) => {
    setPage(1)
    if (person.id) {
      setHighlightPersonId(person.id)
    }
    setDetailPerson(person)
    setDetailOpen(true)
  }

  const handleMerged = (result: MergePeopleOutput) => {
    setDetailPerson(result.person)
    setDetailOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">People directory</h1>
        <p className="text-muted-foreground">
          Search across all people records, verify membership, review participation across shows,
          and consolidate duplicates.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <CardTitle>Directory</CardTitle>
            <CardDescription>
              Search by name or contact details, filter AHSTC members, and open a person to manage
              their membership and see their shows and entries.
            </CardDescription>
          </div>
          <Button type="button" onClick={() => setCreateOpen(true)} className="lg:mt-1">
            Add person
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 items-center gap-3">
              <Input
                aria-label="Search people"
                aria-controls={resultsId}
                aria-describedby={`${resultsId}-status`}
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search people"
                className="lg:max-w-sm"
              />
              <div
                role="group"
                aria-label="Membership filter"
                aria-controls={resultsId}
                aria-describedby={`${resultsId}-status`}
                className="flex items-center gap-2"
              >
                <Button
                  type="button"
                  variant={memberFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setMemberFilter('all')}
                >
                  All
                </Button>
                <Button
                  type="button"
                  variant={memberFilter === 'members' ? 'default' : 'outline'}
                  onClick={() => setMemberFilter('members')}
                >
                  AHSTC
                </Button>
                <Button
                  type="button"
                  variant={memberFilter === 'non-members' ? 'default' : 'outline'}
                  onClick={() => setMemberFilter('non-members')}
                >
                  Non-members
                </Button>
              </div>
            </div>
            {peopleQuery.isFetching && <Skeleton className="h-9 w-28" />}
          </div>

          <LookupResults
            id={resultsId}
            label="People directory results"
            query={peopleQuery}
            lookupKey={params}
            waitingForQuery={searchTerm.trim() !== trimmed}
            loadingMessage="Searching people…"
            forbiddenMessage="You do not have access to view the People directory."
            errorMessage="Unable to search people. Retry to check the directory."
            successMessage={
              people.length === 0
                ? 'No people found. Try a different search or adjust the filters.'
                : `${totalPeople} ${totalPeople === 1 ? 'person' : 'people'} found.`
            }
          >
            {people.length > 0 && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead className="text-center">Shows entered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(people ?? []).map(person => (
                      <TableRow
                        key={person.id ?? person.name}
                        className={cn(
                          highlightPersonId && person.id === highlightPersonId
                            ? 'bg-primary/5'
                            : undefined
                        )}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{person.name}</span>
                            {person.credentialNumber && (
                              <span className="text-xs text-muted-foreground">
                                Credential {person.credentialNumber}
                              </span>
                            )}
                            {person.ahstcMember ? <Badge variant="secondary">AHSTC</Badge> : null}
                          </div>
                        </TableCell>
                        <TableCell>{person.email ?? '—'}</TableCell>
                        <TableCell>{person.phone ?? '—'}</TableCell>
                        <TableCell className="text-center">{person.showsEntered ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" onClick={() => handleOpenDetail(person)}>
                              Manage
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenMerge(person)}
                              disabled={!person.id}
                            >
                              Merge
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableCaption>
                    Showing {people.length} of {totalPeople} people
                    {memberFilter !== 'all' ? ` • filtered: ${memberFilter}` : ''}.
                  </TableCaption>
                </Table>

                <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2 sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={peopleQuery.isFetching || !(pagination?.hasPreviousPage ?? false)}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPage(prev => prev + 1)}
                      disabled={peopleQuery.isFetching || !(pagination?.hasNextPage ?? false)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </LookupResults>
        </CardContent>
      </Card>

      <MergePeopleSheet
        open={mergeOpen}
        onOpenChange={open => setMergeOpen(open)}
        targetPerson={mergeTarget}
        onMerged={handleMerged}
      />

      <PersonDetailSheet
        open={detailOpen}
        onOpenChange={handleDetailOpenChange}
        person={detailPerson}
      />

      <PersonCreateSheet
        open={createOpen}
        onOpenChange={open => setCreateOpen(open)}
        onCreated={handleCreatedPerson}
      />
    </div>
  )
}
